import { debounce, isEmpty } from 'lodash';
import { City } from './types';

const API_BASE = process.env.REACT_APP_PUBLIC_API_URI || 'http://localhost:4000';

async function fetchFromHereMap(endpoint: string): Promise<any> {
    const response = await fetch(`${API_BASE}/here-map/${endpoint}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
        throw new Error(`Here map request failed: ${response.status}`);
    }
    return response.json();
}

function mapToOptions(items: any[], includePostal: boolean): City[] {
    return (items ?? [])
        .filter(item => !!item.address?.city && !!item.address?.postalCode)
        .map(item => ({
            ...item,
            label: `${item.address.city}${includePostal ? ` (${item.address.postalCode})` : ''}`,
            value: item.address.postalCode,
        }));
}

/**
 * Fetch city/postal suggestions. Used by InputCity.
 * When query is numeric, searches postal codes; otherwise searches city names.
 * Label shows city name only (postal shown only when query is numeric).
 */
function fetchCitiesAndPostal(query: string, callback: (data: City[]) => void): void {
    const isPostal = /^\d+$/.test(query);
    const safeQuery = encodeURIComponent(isEmpty(query) ? 'Paris' : query);
    const endpoint = isPostal ? `postals/${safeQuery}` : `cities/${safeQuery}`;

    fetchFromHereMap(endpoint)
        .then(resData => {
            let suggestions = mapToOptions(resData.items, isPostal);
            if (isPostal) {
                suggestions = suggestions.sort((a, b) =>
                    a.address.postalCode.localeCompare(b.address.postalCode),
                );
            }
            callback(suggestions);
        })
        .catch(err => {
            console.error('fetchCitiesAndPostal error:', err);
            callback([]);
        });
}

/**
 * Fetch area suggestions (city + postal code label). Used by InputCity2 and InputPostal.
 * Always shows city name with postal code in label.
 */
function fetchArea(query: string, callback: (data: City[]) => void): void {
    const isPostal = /^\d+$/.test(query);
    const safeQuery = encodeURIComponent(isEmpty(query) ? 'Paris' : query);
    const endpoint = isPostal ? `postals/${safeQuery}` : `cities/${safeQuery}`;

    fetchFromHereMap(endpoint)
        .then(resData => {
            let suggestions = (resData.items ?? [])
                .filter(item => !!item.address?.city && !!item.address?.postalCode)
                .map(item => ({
                    ...item,
                    label: `${item.address.city} (${item.address.postalCode})`,
                    value: item.address.postalCode,
                }));

            if (isPostal) {
                suggestions = suggestions.sort((a, b) =>
                    a.address.postalCode.localeCompare(b.address.postalCode),
                );
            }
            callback(suggestions);
        })
        .catch(err => {
            console.error('fetchArea error:', err);
            callback([]);
        });
}

export const fetchLatLongFromCityPostalCode = async (city: string, postalCode: string): Promise<any> => {
    const safeQuery = encodeURIComponent(`city=${city};postalCode=${postalCode}`);
    return fetchFromHereMap(`detail-by-city-postalcode/${safeQuery}`).catch(err => {
        console.error('fetchLatLongFromCityPostalCode error:', err);
    });
};

export const fetchLatLongFromAddress = async (query: string): Promise<any> => {
    return fetchFromHereMap(`detail-by-address/${encodeURIComponent(query)}`).catch(err => {
        console.error('fetchLatLongFromAddress error:', err);
    });
};

export const searchCitiesAndPostal = debounce(fetchCitiesAndPostal, 500);
export const searchArea = debounce(fetchArea, 500);

export default searchCitiesAndPostal;

